import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, DollarSign, Target, Zap, ArrowRight, CheckCircle, PiggyBank, TrendingDown } from 'lucide-react';

const CostEffectiveSolutionsService = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Banner Section */}
      <section className="relative text-white py-20 md:py-32 overflow-hidden">
        {/* Responsive Background Image */}
        <picture className="absolute inset-0 w-full h-full">
          <source 
            srcSet="/images/hero/cost-effective/desktop_2560x1200.webp" 
            media="(min-width: 1440px)" 
            type="image/webp"
          />
          <source 
            srcSet="/images/hero/cost-effective/tablet_1536x900.webp" 
            media="(min-width: 768px)" 
            type="image/webp"
          />
          <source 
            srcSet="/images/hero/cost-effective/mobile_horiz_1200x960.webp" 
            media="(max-width: 767px) and (orientation: landscape)" 
            type="image/webp"
          />
          <source 
            srcSet="/images/hero/cost-effective/mobile_vert_960x1200.webp" 
            media="(max-width: 767px) and (orientation: portrait)" 
            type="image/webp"
          />
          <img 
            src="/images/hero/cost-effective/desktop_2560x1200.webp"
            alt="Wide green field with wooden fence under a bright blue sky used as hero image for Raven Environmental Cost-Effective Solutions."
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </picture>
        
        {/* Dark Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl text-left mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight drop-shadow-2xl" style={{ textShadow: '2px 2px 8px rgba(0, 0, 0, 0.9)' }}>
              Cost-Effective Environmental Solutions
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-50 leading-relaxed drop-shadow-lg" style={{ textShadow: '1px 1px 6px rgba(0, 0, 0, 0.8)' }}>
              Practical and economical strategies that deliver results without compromising quality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cost-effective-inquiry"
                className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
              >
                Reduce Your Costs
                <DollarSign className="ml-2" size={20} />
              </Link>
              <a
                href="#savings-calculator"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/20 backdrop-blur-sm border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-emerald-700 transition-all shadow-lg"
              >
                Calculate Savings
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Cost-Saving Strategies */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Cost-Saving Approach
            </h2>
            <p className="text-xl text-gray-600">
              Strategic environmental management that protects both your compliance and your budget
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl border-2 border-emerald-100">
              <Target className="w-12 h-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Right-Sized Solutions</h3>
              <p className="text-gray-600 mb-4">
                No over-engineering or unnecessary requirements. We design solutions that meet—but don't exceed—regulatory needs.
              </p>
              <div className="text-2xl font-bold text-emerald-600">Save 30-50%</div>
              <p className="text-sm text-gray-500">vs. over-designed systems</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-xl border-2 border-blue-100">
              <Zap className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Efficient Processes</h3>
              <p className="text-gray-600 mb-4">
                Streamlined permitting and compliance processes that reduce consultant hours and accelerate approvals.
              </p>
              <div className="text-2xl font-bold text-blue-600">40% Faster</div>
              <p className="text-sm text-gray-500">Typical time reduction</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-xl border-2 border-amber-100">
              <TrendingDown className="w-12 h-12 text-amber-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Prevention First</h3>
              <p className="text-gray-600 mb-4">
                Proactive compliance prevents expensive violations, emergency responses, and corrective actions.
              </p>
              <div className="text-2xl font-bold text-amber-600">$100K+</div>
              <p className="text-sm text-gray-500">Average violation cost avoided</p>
            </div>
          </div>
        </div>
      </section>

      {/* Alaska Expertise Advantage */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border-2 border-emerald-200">
            <div className="flex items-start mb-6">
              <div className="bg-emerald-600 p-4 rounded-lg mr-6 flex-shrink-0">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Our Alaska Advantage
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed mb-4">
                  Our significant cost advantage is a direct result of our <strong>specialized Alaskan expertise</strong>, not a reduction in service quality. While traditional consultants often default to expensive, large-scale systems and CEMS monitoring to mitigate their liability in an unfamiliar region, we employ a 'right-sized' approach informed by deep familiarity with ADEC requirements.
                </p>
                <p className="text-xl text-gray-700 leading-relaxed mb-4">
                  This allows us to deliver <strong>full compliance with a fundamentally leaner design</strong>. For example, our Streamlined Engineering and knowledge of approved Efficient Monitoring (Periodic) systems avoids the massive long-term financial burden of CEMS maintenance—a cost that is prohibitively high in remote Alaska.
                </p>
                <p className="text-xl text-gray-700 leading-relaxed">
                  This strategic efficiency is what allows us to save our clients <strong className="text-emerald-700">45-55% on total permitting and initial capital costs</strong>, while ensuring a First-Time Approval and faster path to operation.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 pt-8 border-t border-gray-200">
              <div className="text-left">
                <div className="text-3xl font-bold text-emerald-600 mb-2">45-55%</div>
                <p className="text-sm text-gray-600">Total Cost Savings</p>
              </div>
              <div className="text-left">
                <div className="text-3xl font-bold text-emerald-600 mb-2">13+ Years</div>
                <p className="text-sm text-gray-600">Alaska Expertise</p>
              </div>
              <div className="text-left">
                <div className="text-3xl font-bold text-emerald-600 mb-2">ADEC</div>
                <p className="text-sm text-gray-600">Deep Familiarity</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-left">
            Traditional vs. Cost-Effective Approach
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Traditional Approach - Pain Points */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-red-200">
              <div className="flex items-center mb-6">
                <div className="bg-red-100 p-3 rounded-lg">
                  <TrendingUp className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 ml-4">Traditional Approach</h3>
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-600 italic">Pain Points</p>
              </div>
              <ul className="space-y-3">
                {[
                  { item: 'Title V permit consulting', cost: '$80K - $150K', descriptor: 'High Baseline Cost' },
                  { item: 'Over-designed control systems', cost: '$100K - $300K', descriptor: 'High Capital' },
                  { item: 'Excessive monitoring (CEMS)', cost: '$50K - $120K/yr', descriptor: 'Ongoing Expense' },
                  { item: 'Conservative permit conditions', cost: 'Limited Output', descriptor: 'Operational Limits' },
                  { item: 'Multiple consultant reviews', cost: '$20K - $40K', descriptor: 'Redundant Costs' },
                  { item: 'Lengthy approval processes', cost: 'Lost Revenue', descriptor: 'Delays & Lost Revenue' },
                  { item: 'Gold-plated solutions', cost: '$15K - $30K', descriptor: 'Unnecessary Spending' }
                ].map((item, index) => (
                  <li key={index} className="pb-3 border-b border-gray-200">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-800 font-medium flex-1">{item.item}</span>
                      <span className="text-red-600 font-bold text-base ml-4 whitespace-nowrap">{item.cost}</span>
                    </div>
                    <div className="mt-1">
                      <span className="text-red-500 text-xs italic">({item.descriptor})</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                <p className="text-xl font-bold text-red-700">Major Project Total:</p>
                <p className="text-2xl font-bold text-red-800 mt-1">$200K - $500K+</p>
                <p className="text-sm text-red-600 mt-2">Plus ongoing operational costs</p>
              </div>
            </div>

            {/* Our Approach - Strategic Alaskan Benefit */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-emerald-200">
              <div className="flex items-center mb-6">
                <div className="bg-emerald-100 p-3 rounded-lg">
                  <PiggyBank className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 ml-4">Our Approach</h3>
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-600 italic">Strategic Alaskan Benefit</p>
              </div>
              <ul className="space-y-3">
                {[
                  { item: 'Compliance Certainty & Capital Protection', benefit: '' },
                  { item: 'Capital-Efficient Design', benefit: '30-40% Savings' },
                  { item: 'Maximized Operational Uptime', benefit: 'Lower Operating Costs' },
                  { item: 'Risk-Balanced Permit Conditions', benefit: 'Operational Flexibility' },
                  { item: 'Single-Cycle Review Objective', benefit: 'Reduced Consulting Fees' },
                  { item: 'Projected Schedule Reliability', benefit: 'Quicker Revenue' },
                  { item: 'Focused Compliance Investment', benefit: 'Maximum ROI' }
                ].map((item, index) => (
                  <li key={index} className="pb-3 border-b border-gray-200">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-800 font-semibold flex-1">{item.item}</span>
                    </div>
                    {item.benefit && (
                      <div className="mt-1">
                        <span className="text-emerald-600 text-xs font-semibold">({item.benefit})</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-6 p-4 bg-emerald-50 rounded-lg border-l-4 border-emerald-500">
                <p className="text-xl font-bold text-emerald-700">Optimized Project Economics</p>
                <p className="text-lg font-semibold text-emerald-800 mt-1">(Maximized Lifetime Value)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Potential Savings - Updated Responsive Design */}
      <section id="savings-calculator" className="py-12 sm:py-16 md:py-20 bg-green-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 text-left">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Your Potential Savings</h2>
          <p className="text-base md:text-lg text-green-200 mb-10 max-w-2xl mx-auto">
            Discover how much you could save through our efficient, right-sized environmental solutions.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Cost Reduction Areas */}
            <div className="bg-green-800 p-6 md:p-8 rounded-2xl shadow-lg border-t border-green-800">
              <h3 className="text-xl font-semibold mb-4 text-yellow-300">Cost Reduction Areas</h3>
              <ul className="text-left space-y-2 text-green-100">
                <li><span className="font-bold text-yellow-400">Permit Application Costs:</span> 25–40%</li>
                <li><span className="font-bold text-yellow-400">Compliance Monitoring:</span> 30–50%</li>
                <li><span className="font-bold text-yellow-400">Control Equipment:</span> 20–35%</li>
                <li><span className="font-bold text-yellow-400">Consultant Fees:</span> 30–45%</li>
                <li><span className="font-bold text-yellow-400">Project Timeline:</span> 40–60%</li>
              </ul>
            </div>

            {/* Example Project */}
            <div className="bg-green-800 p-6 md:p-8 rounded-2xl shadow-lg flex flex-col justify-center border-t border-green-800 mt-8 pt-6 md:mt-0 md:pt-8">
              <h3 className="text-xl font-semibold mb-4 text-yellow-300">Example Project</h3>
              <p className="text-green-200 mb-4">
                Traditional approach: <span className="italic">Over-engineered and costly</span><br />
                Our approach: <span className="italic">Streamlined, compliant, and cost-effective</span>
              </p>
              <p className="text-2xl font-bold text-yellow-400 mb-2">Average Savings: 40–50%</p>
              <p className="text-green-300 mb-6">
                Savings vary by project scope — but our focused, right-sized approach ensures reduced cost and faster completion.
              </p>
              <Link
                to="/cost-effective-inquiry"
                className="inline-block bg-yellow-400 text-green-900 font-semibold px-6 py-4 rounded-lg hover:bg-yellow-300 transition w-full sm:w-auto text-left"
              >
                Get Your Custom Estimate →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Real Client Results Section */}
      <section className="py-12 sm:py-16 bg-green-50 text-green-900">
        <div className="container mx-auto px-4 sm:px-6 text-left">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8">Real Cost Savings Examples</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-md hover:shadow-lg transition text-left md:text-left">
              <h3 className="text-green-700 text-xs md:text-sm font-semibold uppercase mb-2">Power Generation</h3>
              <p className="font-bold text-lg md:text-xl mb-1">Title V Permit Renewal</p>
              <p className="text-green-700 text-sm mb-4">Reduced monitoring and equipment costs by <span className="font-semibold">~40%</span>.</p>
            </div>

            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-md hover:shadow-lg transition text-left md:text-left">
              <h3 className="text-green-700 text-xs md:text-sm font-semibold uppercase mb-2">Oil & Gas</h3>
              <p className="font-bold text-lg md:text-xl mb-1">Minor Source Permit</p>
              <p className="text-green-700 text-sm mb-4">Streamlined process cut consulting time by <span className="font-semibold">~50%</span>.</p>
            </div>

            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-md hover:shadow-lg transition text-left md:text-left">
              <h3 className="text-green-700 text-xs md:text-sm font-semibold uppercase mb-2">Seafood Processing</h3>
              <p className="font-bold text-lg md:text-xl mb-1">Boiler Permit + Controls</p>
              <p className="text-green-700 text-sm mb-4">Optimized design achieved <span className="font-semibold">~35% total savings</span>.</p>
            </div>
          </div>

          <div className="mt-10 px-4 md:px-0">
            <p className="text-green-700 text-sm md:text-base max-w-2xl mx-auto">
              These examples show how Raven's practical, compliance-focused engineering consistently lowers costs while maintaining full regulatory integrity.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-green-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <DollarSign className="w-16 h-16 mx-auto mb-6 text-amber-400" />
          <h2 className="text-3xl font-bold mb-4">
            Start Saving on Environmental Compliance
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Get a free cost analysis and discover how much you can save
          </p>
          <Link
            to="/cost-effective-inquiry"
            className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg text-lg"
          >
            Request Free Cost Analysis
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CostEffectiveSolutionsService;